import type { StaffPaymentException } from "@lafayette/schemas";
import { createColumnHelper } from "@tanstack/react-table";

import { formatInstant, shortId } from "../../operations/format";
import { AsyncState } from "../../ui/async-state";
import { Button } from "../../ui/button";
import { DataTable } from "../../ui/data-table";
import { wrapCells } from "../../ui/wrap-cells";
import type { DiagnosticsCapabilities } from "../access";
import { describeDiagnosticsError } from "../errors";
import {
  deliveryReasonLabels,
  exceptionKindLabels,
  type paymentEvidenceLabel,
  receiptCodeLabels,
} from "../labels";
import type { useNotificationFailures, usePaymentExceptions } from "../queries";
import { runbooks } from "../runbooks";
import type { DialogRequest } from "./dialog-state";
import { EvidenceBadge } from "./parts";
import { RecordLink } from "./RecordLink";
import { RunbookLink } from "./RunbookLink";
import { dash, hint, Section } from "./Section";

type ExceptionView = Readonly<{
  item: StaffPaymentException;
  canReconcile: boolean;
  onReconcile: () => void;
  onResolve: () => void;
}>;
const exceptionColumn = createColumnHelper<ExceptionView>();
// No `rowHeader` column here: the kind of a problem repeats (many open exceptions are of one kind), and the time and the payment are not names.
const exceptionColumns = wrapCells([
  exceptionColumn.display({
    id: "opened",
    header: "Opened",
    cell: ({ row }) => formatInstant(row.original.item.openedAt),
  }),
  exceptionColumn.display({
    id: "problem",
    header: "Problem",
    cell: ({ row }) => (
      <>
        {exceptionKindLabels[row.original.item.kind]}
        <span className="block text-xs font-normal text-muted-foreground">
          <code>{row.original.item.kind}</code>
        </span>
      </>
    ),
  }),
  exceptionColumn.display({
    id: "payment",
    header: "Payment",
    cell: ({ row }) =>
      row.original.item.paymentRequestId === null ? (
        dash
      ) : (
        <RecordLink type="payment_request" id={row.original.item.paymentRequestId} />
      ),
  }),
  exceptionColumn.display({
    id: "action",
    header: "Action",
    cell: ({ row }) => (
      <span className="flex flex-wrap gap-2">
        {row.original.item.paymentRequestId !== null && row.original.canReconcile ? (
          <Button type="button" variant="soft" size="sm" onClick={row.original.onReconcile}>
            Reconcile{" "}
            <span className="sr-only">payment of exception {shortId(row.original.item.id)}</span>
          </Button>
        ) : null}
        <Button type="button" variant="outline" size="sm" onClick={row.original.onResolve}>
          Resolve <span className="sr-only">exception {shortId(row.original.item.id)}</span>
        </Button>
      </span>
    ),
  }),
]);

type DeliveryView = Readonly<{
  item: NonNullable<
    ReturnType<typeof useNotificationFailures>["data"]
  >["pages"][number]["items"][number];
}>;
const deliveryColumn = createColumnHelper<DeliveryView>();
// No `rowHeader` column here: the kind of a notification repeats (many deliveries of one kind fail), so no cell of a row names it alone.
const deliveryColumns = wrapCells([
  deliveryColumn.display({
    id: "when",
    header: "When",
    cell: ({ row }) => formatInstant(row.original.item.occurredAt),
  }),
  deliveryColumn.display({
    id: "notification",
    header: "Notification",
    cell: ({ row }) => (
      <>
        <code>{row.original.item.kind}</code>
        <span className="text-xs font-normal text-muted-foreground">
          {" "}
          {row.original.item.audience === "STAFF" ? "to staff" : "to a customer"}
        </span>
      </>
    ),
  }),
  deliveryColumn.display({
    id: "reason",
    header: "Reason",
    cell: ({ row }) => {
      const { item } = row.original;
      return (
        <>
          {deliveryReasonLabels[item.reasonCode] ?? "Failed"} <code>{item.reasonCode}</code>
          {item.receiptCodes.length > 0 && (
            <ul
              className="m-0 mt-1 grid list-none gap-0.5 p-0 text-xs text-muted-foreground"
              aria-label="Receipt codes"
            >
              {item.receiptCodes.map((code) => (
                <li key={code}>
                  {receiptCodeLabels[code] ?? "Another receipt error"} <code>{code}</code>
                </li>
              ))}
            </ul>
          )}
        </>
      );
    },
  }),
  deliveryColumn.display({
    id: "attempts",
    header: "Attempts",
    meta: { className: "tabular-nums" },
    cell: ({ row }) => row.original.item.attempts,
  }),
  deliveryColumn.display({
    id: "about",
    header: "About",
    cell: ({ row }) => (
      <RecordLink type={row.original.item.subject.type} id={row.original.item.subject.id} />
    ),
  }),
]);

/** 4. Payment problems: the evidence label and the open exceptions. */
export function PaymentProblemsSection({
  exceptions,
  evidence,
  capabilities,
  onOpen,
}: Readonly<{
  exceptions: ReturnType<typeof usePaymentExceptions>;
  evidence: ReturnType<typeof paymentEvidenceLabel>;
  capabilities: DiagnosticsCapabilities;
  onOpen: (dialog: DialogRequest) => void;
}>) {
  const exceptionViews: ExceptionView[] = (exceptions.data?.items ?? []).map((item) => ({
    item,
    canReconcile: capabilities.reconcile,
    onReconcile: () =>
      onOpen({ kind: "reconcile", paymentRequestId: item.paymentRequestId as string }),
    onResolve: () => onOpen({ kind: "resolve", exception: item }),
  }));

  return (
    <Section
      id="payment-exceptions"
      titleId="diag-payments"
      title="Payment problems"
      runbookLinks={
        <>
          <RunbookLink to={runbooks.paymentReconcile}>Runbook: payment reconcile</RunbookLink>
          <RunbookLink to={runbooks.catalogPublish}>
            Runbook: accidental catalog publish
          </RunbookLink>
        </>
      }
    >
      <p className="m-0 flex flex-wrap items-center gap-x-3 gap-y-1">
        <EvidenceBadge evidence={evidence} />
        <span className="text-sm text-muted-foreground">
          {evidence === "FAKE"
            ? "The payment provider is the fake gateway. A fake result is never launch evidence."
            : evidence === "REAL"
              ? "The payment provider is the real one."
              : "No payment provider is set up."}
        </span>
      </p>
      {!capabilities.resolveException ? (
        <p className={hint}>
          Only a supervisor sees and resolves payment exceptions. Only a supervisor, or a concierge
          who covers the customer, reconciles a payment.
        </p>
      ) : exceptions.isPending ? (
        <AsyncState
          kind="loading"
          title="Loading the exceptions"
          message="Waiting for the answer."
        />
      ) : exceptions.isError ? (
        <AsyncState
          kind="error"
          title="The exceptions could not be read"
          message={describeDiagnosticsError(exceptions.error).message}
          onRetry={() => void exceptions.refetch()}
        />
      ) : exceptions.data.items.length === 0 ? (
        <AsyncState
          kind="empty"
          title="No open exception"
          message="No payment needs a human decision."
        />
      ) : (
        <DataTable<ExceptionView>
          caption="Open payment reconciliation exceptions"
          data={exceptionViews}
          getRowId={(view) => view.item.id}
          rowAttributes={(view) => ({ "data-exception": view.item.id })}
          columns={exceptionColumns}
          banded="strong"
          className="rounded-md"
        />
      )}
    </Section>
  );
}

/** 5. Notification failures: the deliveries that ended in a failure nobody tried again. */
export function NotificationFailuresSection({
  notifications,
}: Readonly<{
  notifications: ReturnType<typeof useNotificationFailures>;
}>) {
  const deliveries = notifications.data?.pages.flatMap((page) => page.items) ?? [];
  const deliveryViews: DeliveryView[] = deliveries.map((item) => ({ item }));

  return (
    <Section
      id="notification-failures"
      titleId="diag-notifications"
      title="Notification failures"
      runbookLinks={
        <RunbookLink to={runbooks.notificationFailure}>Runbook: notification failure</RunbookLink>
      }
    >
      {notifications.isPending ? (
        <AsyncState kind="loading" title="Loading the failures" message="Waiting for the answer." />
      ) : notifications.isError && notifications.data === undefined ? (
        <AsyncState
          kind="error"
          title="The notification failures could not be read"
          message={describeDiagnosticsError(notifications.error).message}
          onRetry={() => void notifications.refetch()}
        />
      ) : deliveries.length === 0 ? (
        <AsyncState
          kind="empty"
          title="No failed notification"
          message="No push notification ended in a failure that was not tried again."
        />
      ) : (
        <>
          <p className={hint}>
            A suppressed push (quiet hours, a person's settings, no device) is not a failure and is
            not listed. To try a failed delivery again, re-run its{" "}
            <strong>Notification: deliver</strong> job above. A row with a receipt code was taken by
            Expo: it is final, and a re-run does not send it again.
          </p>
          <DataTable<DeliveryView>
            caption="Failed notification deliveries"
            data={deliveryViews}
            getRowId={(view) => view.item.deliveryId}
            rowAttributes={(view) => ({ "data-delivery": view.item.deliveryId })}
            columns={deliveryColumns}
            banded="strong"
            className="rounded-md"
          />
          {notifications.hasNextPage ? (
            <div>
              <Button
                type="button"
                variant="outline"
                disabled={notifications.isFetchingNextPage}
                onClick={() => void notifications.fetchNextPage()}
              >
                {notifications.isFetchingNextPage ? "Loading…" : "Load more failures"}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Section>
  );
}
